<template>
    <div
        v-click-outside="close"
        class="cs"
        :class="{ 'cs--collapsed': collapsed, 'cs--open': open }"
    >
        <button
            type="button"
            class="cs-trigger"
            :class="{ 'cs-trigger--collapsed': collapsed }"
            :title="currentName"
            :aria-expanded="open ? 'true' : 'false'"
            @click="toggle"
        >
            <span class="cs-trigger-avatar" aria-hidden="true">{{ initials }}</span>
            <span v-if="!collapsed" class="cs-trigger-text">
                <span class="cs-trigger-hint">{{ $t('admin.companies.switcher_hint') || 'Compagnie active' }}</span>
                <span class="cs-trigger-name">{{ currentName }}</span>
            </span>
            <i v-if="!collapsed" class="fal fa-chevron-down cs-trigger-caret"></i>
        </button>

        <transition name="cs-dropdown">
            <div v-if="open" class="cs-panel" role="menu">
                <div v-if="companies.length > 6" class="cs-search">
                    <i class="fal fa-search"></i>
                    <input
                        ref="searchInput"
                        v-model="query"
                        type="text"
                        class="cs-search-input"
                        :placeholder="$t('admin.companies.search_placeholder')"
                    />
                </div>
                <div class="cs-list">
                    <button
                        v-for="company in filteredCompanies"
                        :key="company.id"
                        type="button"
                        class="cs-item"
                        :class="{ 'cs-item--active': company.id === currentId }"
                        @click="pick(company)"
                    >
                        <span class="cs-item-avatar" aria-hidden="true">{{ initialsFor(company) }}</span>
                        <span class="cs-item-body">
                            <span class="cs-item-name">{{ company.name }}</span>
                            <span v-if="company.slug" class="cs-item-slug">/{{ company.slug }}</span>
                        </span>
                        <i v-if="company.id === currentId" class="fal fa-check cs-item-check"></i>
                    </button>
                    <p v-if="!filteredCompanies.length" class="cs-empty">
                        {{ $t('admin.no_element') }}
                    </p>
                </div>
                <div v-if="loading" class="cs-loading">
                    <i class="fal fa-spinner-third fa-spin"></i>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions, mapState } from 'vuex'

function clickOutside(el, binding) {
    el.__clickOutsideHandler__ = (event) => {
        if (!(el === event.target || el.contains(event.target))) {
            binding.value(event)
        }
    }
    document.addEventListener('click', el.__clickOutsideHandler__)
}

export default {
    name: 'CompanySwitcher',
    directives: {
        clickOutside: {
            bind: clickOutside,
            unbind(el) {
                document.removeEventListener('click', el.__clickOutsideHandler__)
                delete el.__clickOutsideHandler__
            }
        }
    },
    props: {
        collapsed: { type: Boolean, default: false }
    },
    data() {
        return {
            open: false,
            loading: false,
            query: ''
        }
    },
    computed: {
        ...mapState('auth', { currentCompany: (state) => state.company }),
        ...mapState('admin', { companiesPaginator: (state) => state.companies }),
        companies() {
            if (!this.companiesPaginator) return []
            if (Array.isArray(this.companiesPaginator)) return this.companiesPaginator
            return this.companiesPaginator.data || []
        },
        currentId() {
            return this.currentCompany ? this.currentCompany.id : null
        },
        currentName() {
            return this.currentCompany ? this.currentCompany.name : '—'
        },
        initials() {
            return this.initialsFor(this.currentCompany)
        },
        filteredCompanies() {
            const q = this.query.trim().toLowerCase()
            if (!q) return this.companies
            return this.companies.filter((c) => {
                const name = (c.name || '').toLowerCase()
                const slug = (c.slug || '').toLowerCase()
                return name.includes(q) || slug.includes(q)
            })
        }
    },
    methods: {
        ...mapActions('admin', ['getCompanies', 'switchCompany']),
        initialsFor(company) {
            const source = (company && company.name) ? company.name : '?'
            const parts = source.split(/\s+/).filter(Boolean)
            return ((parts[0] || '').charAt(0) + (parts[1] || '').charAt(0)).toUpperCase() || '?'
        },
        toggle() {
            if (this.open) {
                this.close()
                return
            }
            this.open = true
            this.ensureLoaded()
            this.$nextTick(() => {
                if (this.$refs.searchInput) this.$refs.searchInput.focus()
            })
        },
        close() {
            this.open = false
            this.query = ''
        },
        ensureLoaded() {
            if (this.companies.length || this.loading) return
            this.loading = true
            this.getCompanies()
                .catch(() => {})
                .finally(() => {
                    this.loading = false
                })
        },
        pick(company) {
            if (!company || company.id === this.currentId) {
                this.close()
                return
            }
            this.switchCompany(company)
        }
    }
}
</script>

<style scoped>
.cs {
    position: relative;
    padding: 10px;
    border-bottom: 1px solid var(--v2-border-subtle);
}

.cs--collapsed {
    padding: 8px;
}

.cs-trigger {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    background: var(--v2-surface-1);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    color: var(--v2-text-1);
    cursor: pointer;
    transition: all var(--v2-transition-fast);
    font-family: inherit;
    text-align: left;
}

.cs-trigger:hover {
    border-color: var(--v2-primary);
    background: var(--v2-primary-soft);
}

.cs--open .cs-trigger {
    border-color: var(--v2-primary);
    background: var(--v2-primary-soft);
}

.cs-trigger--collapsed {
    justify-content: center;
    padding: 6px;
}

.cs-trigger-avatar {
    width: 28px;
    height: 28px;
    border-radius: var(--v2-radius-sm);
    background: var(--v2-primary);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.72rem;
    font-weight: var(--v2-font-weight-bold);
    flex-shrink: 0;
    letter-spacing: -0.02em;
}

.cs-trigger-text {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
    flex: 1;
}

.cs-trigger-hint {
    font-size: 0.62rem;
    color: var(--v2-text-3);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: var(--v2-font-weight-bold);
}

.cs-trigger-name {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.cs-trigger-caret {
    font-size: 0.7rem;
    color: var(--v2-text-3);
    flex-shrink: 0;
    transition: transform 200ms ease;
}

.cs--open .cs-trigger-caret {
    transform: rotate(180deg);
    color: var(--v2-primary);
}

.cs-panel {
    position: absolute;
    top: calc(100% + 4px);
    left: 10px;
    right: 10px;
    z-index: 40;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    box-shadow: var(--v2-shadow-lg);
    overflow: hidden;
    max-height: 380px;
    display: flex;
    flex-direction: column;
}

.cs--collapsed .cs-panel {
    left: calc(100% + 6px);
    right: auto;
    min-width: 240px;
    top: 6px;
}

.cs-search {
    position: relative;
    padding: 10px;
    border-bottom: 1px solid var(--v2-border-subtle);
}

.cs-search i {
    position: absolute;
    left: 22px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--v2-text-3);
    font-size: 0.8rem;
    pointer-events: none;
}

.cs-search-input {
    width: 100%;
    box-sizing: border-box;
    height: 32px;
    padding: 0 10px 0 32px;
    background: var(--v2-surface-1);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-sm);
    font-size: var(--v2-text-sm-size);
    outline: none;
    transition: border-color var(--v2-transition-fast), box-shadow var(--v2-transition-fast);
}

.cs-search-input:focus {
    border-color: var(--v2-primary);
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.12);
}

.cs-list {
    overflow-y: auto;
    padding: 4px;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.cs-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    background: transparent;
    border: none;
    border-radius: var(--v2-radius-sm);
    color: var(--v2-text-1);
    cursor: pointer;
    transition: background var(--v2-transition-fast);
    font-family: inherit;
    text-align: left;
    width: 100%;
}

.cs-item:hover {
    background: var(--v2-surface-2);
}

.cs-item--active {
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
}

.cs-item-avatar {
    width: 26px;
    height: 26px;
    border-radius: var(--v2-radius-sm);
    background: var(--v2-surface-2);
    color: var(--v2-text-2);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.68rem;
    font-weight: var(--v2-font-weight-bold);
    flex-shrink: 0;
    letter-spacing: -0.02em;
}

.cs-item--active .cs-item-avatar {
    background: var(--v2-primary);
    color: #fff;
}

.cs-item-body {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
    flex: 1;
}

.cs-item-name {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.cs-item-slug {
    font-size: 0.7rem;
    color: var(--v2-text-3);
    font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
}

.cs-item-check {
    color: var(--v2-primary);
    font-size: 0.78rem;
    flex-shrink: 0;
}

.cs-empty {
    padding: 14px;
    text-align: center;
    color: var(--v2-text-4);
    font-size: var(--v2-text-xs-size);
    margin: 0;
}

.cs-loading {
    padding: 10px;
    display: flex;
    justify-content: center;
    color: var(--v2-text-3);
    font-size: 0.9rem;
    border-top: 1px solid var(--v2-border-subtle);
}

/* Transition */
.cs-dropdown-enter-active,
.cs-dropdown-leave-active {
    transition: opacity 160ms ease, transform 160ms cubic-bezier(0.22, 1, 0.36, 1);
    transform-origin: top center;
}

.cs-dropdown-enter,
.cs-dropdown-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}
</style>
